import { Flex, Text, SimpleGrid, Anchor } from '@mantine/core';
import { DetailField } from './DetailField';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import type { CurrencyType } from '@dealstack/shared/lib/generated';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { joinPartyNames } from './partyNames';

interface TransactionFields {
	buyers: Array<{ name: string }>;
	sellers: Array<{ name: string }>;
	securityType: string | null;
	transactionType: string;
	transactionDate: string | null;
	description: string;
	numberOfUnits: number;
	priceCost: { value: number; currency: string };
	buyerHolding?: string | null;
}

interface Props {
	transaction: TransactionFields;
	hasFile: boolean;
	onViewInDocument?: (e?: React.MouseEvent) => void;
}

export const TransactionDetailsGrid = ({ transaction, hasFile, onViewInDocument }: Props) => {
	const tCapManage = useTranslationCapManage();
	const buyerNames = joinPartyNames(transaction.buyers);
	const sellerNames = joinPartyNames(transaction.sellers);
	const costCurrency = transaction.priceCost?.currency as CurrencyType | undefined;
	const viewLabel = tCapManage('batch_analysis.view_in_document');
	const viewHandler = hasFile ? onViewInDocument : undefined;

	return (
		<SimpleGrid cols={4} spacing="md">
			<DetailField
				label={tCapManage('batch_analysis.detail_panel.buyer')}
				value={buyerNames}
				onViewInDocument={viewHandler}
				viewInDocumentLabel={viewLabel}
			/>
			<DetailField
				label={tCapManage('batch_analysis.detail_panel.seller')}
				value={sellerNames}
				onViewInDocument={viewHandler}
				viewInDocumentLabel={viewLabel}
			/>
			<DetailField
				label={tCapManage('batch_analysis.detail_panel.security')}
				value={transaction.securityType || tCapManage('batch_analysis.unknown')}
				onViewInDocument={viewHandler}
				viewInDocumentLabel={viewLabel}
			/>
			<DetailField
				label={tCapManage('batch_analysis.detail_panel.type')}
				value={transaction.transactionType || LONG_DASH}
				onViewInDocument={viewHandler}
				viewInDocumentLabel={viewLabel}
			/>
			<Flex direction="column" gap={2}>
				<Text size="xs" color="dimmed">
					{tCapManage('batch_analysis.detail_panel.cost')}
				</Text>
				<Flex align="center" gap={4}>
					<AmountFormatted
						value={transaction.priceCost?.value}
						currency={costCurrency}
						showZero
					/>
					{hasFile && onViewInDocument && (
						<Anchor
							size="xs"
							component="button"
							color="dimmed"
							onClick={onViewInDocument}
						>
							{viewLabel}
						</Anchor>
					)}
				</Flex>
			</Flex>
			<DetailField
				label={tCapManage('batch_analysis.detail_panel.number_of_units')}
				value={
					transaction.numberOfUnits > 0
						? transaction.numberOfUnits.toLocaleString()
						: LONG_DASH
				}
				onViewInDocument={viewHandler}
				viewInDocumentLabel={viewLabel}
			/>
			<DetailField
				label={tCapManage('batch_analysis.detail_panel.date')}
				value={transaction.transactionDate || LONG_DASH}
				onViewInDocument={viewHandler}
				viewInDocumentLabel={viewLabel}
			/>
			<DetailField
				label={tCapManage('batch_analysis.detail_panel.description')}
				value={transaction.description || LONG_DASH}
				onViewInDocument={viewHandler}
				viewInDocumentLabel={viewLabel}
			/>
		</SimpleGrid>
	);
};
